import type { Shopify } from '@jackboxgames/shopify'
import storefrontsJson from '$services/storefronts/storefronts.json'
import type { JBGWeb } from '$types/JBGWeb'
import { currentCountry } from '$services/shopify'
import { I18n } from '$services/i18n'
import { combineUtmParams } from '$services/tracking'

export function getCurrencySymbol(currencyCode: string): string {
    switch (currencyCode) {
    case 'EUR':
        return '€'
    case 'GBP':
        return '£'
    case 'CAD':
    case 'MXN':
    case 'USD':
    default:
        return '$'
    }
}

// eslint-disable-next-line complexity
function getPlatformInfo(key: string): string[] | null {
    if (!key) return null

    let displayText = ''
    let iconFileName = ''
    switch (key) {
    case 'steam':
        displayText = 'Steam'
        iconFileName = 'steam'
        return [displayText, iconFileName]
    case 'google':
        displayText = 'Google Play'
        iconFileName = 'google'
        return [displayText, iconFileName]
    case 'humble':
        displayText = 'Humble Bundle'
        iconFileName = 'humble'
        return [displayText, iconFileName]
    case 'nintendo':
        displayText = 'Nintendo Switch'
        iconFileName = 'nintendo-switch'
        return [displayText, iconFileName]
    case 'microsoft':
        displayText = 'Xbox'
        iconFileName = 'xbox'
        return [displayText, iconFileName]
    case 'sony':
        displayText = 'Playstation'
        iconFileName = 'ps'
        return [displayText, iconFileName]
    case 'apple':
        displayText = 'Apple TV & iPad'
        iconFileName = 'apple'
        return [displayText, iconFileName]
    case 'apple-mac':
        displayText = 'Mac App Store'
        iconFileName = 'mas'
        return [displayText, iconFileName]
    case 'epic':
        displayText = 'Epic Games Store'
        iconFileName = 'egs'
        return [displayText, iconFileName]
    case 'amazon':
        displayText = 'Amazon Fire TV'
        iconFileName = 'amazon'
        return [displayText, iconFileName]
    case 'luna':
        displayText = 'Amazon Luna'
        iconFileName = 'luna'
        return [displayText, iconFileName]
    case 'shopify':
        displayText = 'Jackbox Shop'
        iconFileName = 'shopify'
        return [displayText, iconFileName]
    default:
        // TODO: fallbacks text and icon?
        return [displayText, iconFileName]
    }
}

export function getStorefrontsFromProductData(product: Shopify.Product) {
    if (!product) return

    const platforms: JBGWeb.PlatformOption[] = []
    const shopifyMetafieldsArr: Shopify.Metafield[] = product.metafields

    if (!shopifyMetafieldsArr) return console.warn('found no metafields on product')

    // for packs this should always just be an array of 1
    let fields: Shopify.ReferenceField[] = []
    shopifyMetafieldsArr.forEach((metafieldsObj) => {
        if (!metafieldsObj) return console.warn('found no fields in metafields object')
        fields = metafieldsObj.reference.fields
    })

    platforms.push({
        currencyCode: product.variants[0].price.currency_code,
        price: product.variants[0].price.amount,
        icon: 'shopify',
        vendor: 'shopify',
        text: 'Jackbox Shop'
    })
    fields.forEach((field: Shopify.ReferenceField) => {
        if (field.key !== 'market') {
            const platform: JBGWeb.PlatformOption = {
                text: null,
                icon: null,
                vendor: null,
                price: '',
                currencyCode: null
            }

            platform.vendor = field.key

            // destructure field.value
            // which comes in like this from shopify: "{\"amount\":\"29.99\",\"currency_code\":\"USD\"}"

            const json = JSON.parse(field.value)
            // do not include storefront when price is 0.0
            if (json.amount !== '0.0') {
                platform.price = json.amount as string
                platform.currencyCode = json.currency_code
                const platformInfo = getPlatformInfo(field.key)
                if (platformInfo) {
                    platform.text = platformInfo[0]
                    platform.icon = platformInfo[1]
                }

                platforms.push(platform)
            }
        }
    })

    // sort platforms in order of sale priority (as determined by marketing)
    const sortingTemplate = ['shopify', 'nintendo', 'microsoft', 'sony', 'steam', 'apple', 'amazon', 'epic', 'humble']
    const sort = sortingTemplate.map((i) => platforms.find((j) => j.vendor === i))
    // not all packs will have a value for each entry in the sortingTemplate,
    // so remove all undefined entries.
    const sortedPlatforms = sort.filter((i) => i !== undefined)

    return sortedPlatforms
}

function standardizePlatform(platform: string) {
    // Standardize platform names
    // This happens because of a discrepancy between our PlatformIcon names and the platform abbreviations in storefronts.json
    switch (platform) {
    case 'xbox':
        platform = 'microsoft'
        break
    case 'ps':
        platform = 'sony'
        break
    case 'nintendo-switch':
        platform = 'nintendo'
        break
    case 'egs':
        platform = 'epic'
        break
    }
    return platform
}

export function getUID(platform: string, slug: string, region: string) {
    if (!storefrontsJson[platform].titles[slug]) return ''
    let id = storefrontsJson[platform].titles[slug].id

    const naRegions = ['US', 'CA', 'MX']
    if (!naRegions.includes(region)) {
        const intlId = storefrontsJson[platform].titles[slug].intlId
        if (intlId) {
            id = intlId
        }
    }
    if (!id) return ''

    return id
}

function getRegion() {
    let region = 'US' // default US
    const storefrontRegionList = ['US', 'GB', 'DE', 'FR', 'IT', 'ES', 'MX', 'BR']
    if (storefrontRegionList.includes(currentCountry.code)) {
        region = currentCountry.code
    }
    return region
}

function steamLocaleParam(region: string) {
    switch (region) {
    case 'DE':
        return '?l=german'
    case 'FR':
        return '?l=french'
    case 'IT':
        return '?l=italian'
    case 'ES':
        return '?l=spanish'
    case 'MX':
        return '?l=latam'
    case 'BR':
        return '?l=brazilian'
    default: return ''
    }
}

function epicLocaleParam(region: string) {
    switch (region) {
    case 'DE':
        return '?lang=de'
    case 'FR':
        return '?lang=fr'
    case 'IT':
        return '?lang=it'
    case 'ES':
        return '?lang=es-ES'
    case 'MX':
        return '?lang=es-MX'
    case 'BR':
        return '?lang=pt-BR'
    default: return ''
    }
}

function getBaseUrl(platform: string, region: string): string {
    const r = `${region}Url`
    return storefrontsJson[platform][r]
}

// eslint-disable-next-line complexity, max-len
export function getStorefrontLink(locale: string, slug: string, platform: string, _fellBackToEn = false, isBundle = false, _isLocalized = false): string | undefined {
    if (!platform || !slug || platform === 'shopify') return

    platform = standardizePlatform(platform)

    let lookupSlug = slug
    if (_isLocalized) {
        const slugEnding = slug.split('-').reverse()[0]
        if (locale !== 'en' && !_fellBackToEn && I18n.isSupported(slugEnding)) {
            // remove localized slug endings; ex: '-de'
            slug = slug.slice(0, -(locale.length + 1))
            lookupSlug = slug
        }
    }

    const region = getRegion()
    const UID = getUID(platform, lookupSlug, region)
    const baseUrl = getBaseUrl(platform, region)
    let nonUIDRegions = [] // used in some cases to list what regions do not contain a UID in their url

    let url: URL
    switch (platform) {
    case 'google':
        url = new URL(`${UID}`, baseUrl)
        break
    case 'humble':
        if (lookupSlug === 'drawful-2') {
            slug = 'drawful-two'
        }

        if (lookupSlug === 'the-jackbox-party-pack-1') {
            slug = 'the-jackbox-party-pack'
        }

        if (lookupSlug === 'fibbage') {
            slug = 'fibbage-xl'
        }

        if (lookupSlug === 'ydkj-classic') {
            slug = 'you-dont-know-jack-bundle'
        }

        url = new URL(slug, baseUrl)
        break
    case 'steam':
        url = new URL(`app/${UID}/${slug.replace(/-/g, '_')}`, baseUrl)
        // BUNDLES
        if (isBundle) {
            url = new URL(`bundle/${UID}/${slug.replace(/-/g, '_')}`, baseUrl)
        }
        nonUIDRegions = ['US', 'UK']
        if (!nonUIDRegions.includes(region)) {
            url = new URL(`app/${UID}/${slug.replace(/-/g, '_')}/${steamLocaleParam(region)}`, baseUrl)
            // BUNDLES
            if (isBundle) {
                url = new URL(`bundle/${UID}/${slug.replace(/-/g, '_')}/${steamLocaleParam(region)}`, baseUrl)
            }
            // YDKJ Classic
            if (lookupSlug === 'ydkj-classic') {
                url = new URL(`sub/${UID}/${slug.replace(/-/g, '_')}/${steamLocaleParam(region)}`, baseUrl)
            }
        }
        // YDKJ Classic
        if (lookupSlug === 'ydkj-classic') {
            url = new URL(`sub/${UID}/${slug.replace(/-/g, '_')}`, baseUrl)
        }
        break
    case 'nintendo':
        if (lookupSlug === 'the-jackbox-party-pack-1') {
            slug = 'the-jackbox-party-pack'
        }
        if (lookupSlug === 'quiplash-2-interlashional') {
            slug = 'quiplash-2-interlashional-the-say-anything-party-game'
        }
        if (lookupSlug === 'fibbage') {
            slug = 'fibbage-xl'
        }
        if (lookupSlug === 'the-jackbox-party-trilogy-2') {
            slug = 'the-jackbox-party-trilogy-2-0'
        }
        if (lookupSlug === 'the-jackbox-party-trilogy-3') {
            slug = 'the-jackbox-party-trilogy-3-0'
        }
        if (lookupSlug === 'the-jackbox-naughty-pack') {
            slug = slug.slice(4) // remove 'the-'
        }
        if (lookupSlug === 'the-jackbox-party-quintpack-2') {
            slug = 'the-jackbox-party-quintpack-2-0'
        }
        url = new URL(`${slug}-switch`, baseUrl)
        nonUIDRegions = ['US', 'MX', 'BR']
        if (!nonUIDRegions.includes(region)) {
            url = new URL(`${slug}-switch-${UID}.html`, baseUrl)
        }
        break
    case 'microsoft':
        if (lookupSlug === 'quiplash-2-interlashional') {
            slug = 'quiplash-2-interlashional-the-say-anything-party-game'
        }
        if (lookupSlug === 'fibbage') {
            slug = 'fibbage-the-hilarious-bluffing-party-game'
        }
        url = new URL(`${slug}/${UID}`, baseUrl)
        break
    case 'epic':
        if (lookupSlug !== 'the-jackbox-survey-scramble'
            && lookupSlug !== 'the-jackbox-party-pack-10'
            && lookupSlug !== 'the-jackbox-party-pack-11'
            && lookupSlug !== 'the-jackbox-party-pack-12'
            && lookupSlug.includes('the-')
        ) {
            slug = slug.slice(4)
        }
        if (lookupSlug === 'trivia-murder-party-3') {
            slug = 'trivia-murder-party'
        }

        url = new URL(`${slug}${UID}`, baseUrl)
        nonUIDRegions = ['US', 'UK']
        if (!nonUIDRegions.includes(region)) {
            url = new URL(`${slug}${UID}/${(epicLocaleParam(region))}`, baseUrl)
        }
        break
    case 'sony':
        if (
            lookupSlug === 'the-jackbox-party-pack-12'
            || lookupSlug === 'the-jackbox-party-pack-10'
            || lookupSlug === 'the-jackbox-party-pack-9'
            || lookupSlug === 'the-jackbox-survey-scramble'
        ) {
            url = new URL(`concept/${UID}`, baseUrl)
        } else {
            url = new URL(`product/${UID}`, baseUrl)
        }
        break
    case 'amazon':
        url = new URL(`dp/${UID}`, baseUrl)
        if (lookupSlug === 'fibbage') {
            slug = 'Fibbage-Hilarious-Bluffing-Party-Game'
        }
        url = new URL(`${slug}/${UID}`, baseUrl)
        break
    case 'luna':
        url = new URL('https://www.amazon.com/luna/detail/B09R97C6ZG?ref_=jbx')
        break
    case 'apple':
        url = new URL(`${slug}/${UID}`, baseUrl)
        break
    case 'mas':
        url = new URL(`${slug}/${UID}`, baseUrl)
        break
    }

    const route = useRoute()
    if (url) {
        combineUtmParams(url, new URLSearchParams(route.query as Record<string, string>))
    }

    return url.href
}

export function getShopifyLink(id: string) {
    const baseUrl = getBaseUrl('jbg', getRegion())
    return new URL(`${id}`, baseUrl).toString()
}

// returns a class that is used in GTM by marketing on elements that link to external storefront
export function getProductGTMClass(slug: string): string {
    if (!slug) return ''

    // Special cases
    switch (slug) {
    case 'the-jackbox-survey-scramble':
        return 'survey-scramble'
    case 'the-jackbox-naughty-pack':
        return 'jnp'
    case 'the-jackbox-party-starter':
        return 'party-starter'
    case 'the-jackbox-decade-bundle':
        return 'decade-bundle'
    }

    let gtmSlug = slug
    // for most other products we can just slice off 'the-jackbox-party-'
    const partyPackRegex = /^the-jackbox-party-/
    if (partyPackRegex.test(slug)) {
        gtmSlug = slug.replace(partyPackRegex, '')
    }

    return gtmSlug
}

// TODO: this shouldn't need to be exported after ENG-1682 is done
export function formatPrice(price: string, currencyCode: string, locale: string): string {
    const formattedPrice = new Intl.NumberFormat(
        locale,
        {
            minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: currencyCode
        }
    ).format(Number(price))
    return formattedPrice
}

export function localizedPriceString(price: string, currencyCode: string, locale: string): string | undefined {
    // TODO: consider re-adding logic to display prices based on Geolocation
    // // use cloudfront cookie to map to supported shopify currencyCode
    // const region = Geolocate.getCountryCode()
    // const defaultCurrencyCode = currencyCode // default to countryCode from shopify data
    // let mappedCurrencyCode = defaultCurrencyCode
    // Object.entries(currencyCodeMapJson).forEach((code) => {
    //     if (code[1].includes(region)) {
    //         mappedCurrencyCode = code[0]
    //     }
    // })
    // return formatPrice(price, mappedCurrencyCode, locale)

    // TODO: Until ENG-1682 is done: force USD
    return formatPrice(price, 'USD', locale)
}

// TODO: remove this temporary function after ENG-1682 is done:
export function isEurCountry(): boolean {
    const eurCountries = ['AS', 'AD', 'AT', 'BE', 'FI', 'FR', 'GF', 'TF', 'DE', 'GR', 'GP', 'IE', 'IT',
        'LU', 'MQ', 'YT', 'MC', 'NL', 'PT', 'RE', 'WS', 'SM', 'SI', 'ES', 'VA']
    return (eurCountries.includes(currentCountry.code))
}
